import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import UserAvatar from './common/UserAvatar';
import {
  AR_COMMUNICATION_STATUSES,
  AR_COMMUNICATION_ORDER,
  AR_COMMUNICATION_ADMIN_ONLY,
  AR_COMMUNICATION_LABELS,
  AR_COMMUNICATION_COLORS
} from '../utils/statusConstants';

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const StatusDot = ({ status }) => (
  <Box
    component="span"
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: AR_COMMUNICATION_COLORS[status] || '#9e9e9e',
      display: 'inline-block',
      flexShrink: 0
    }}
  />
);

const StatusLabel = ({ status }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <StatusDot status={status} />
    {AR_COMMUNICATION_LABELS[status] || status}
  </Box>
);

/**
 * AR line "Communications" status (team communication only — unrelated to
 * the line's Status column). Partners can set New/Wait/Charge; Super Admins
 * can also set Invoiced/Pushed/Pulled. Shows the last changer's avatar and a full
 * change log behind the info button.
 *
 * `record` is the ar_communications doc ({ status, history: [...] }) or null.
 */
const ARCommunicationCell = ({ record, isAdmin, onChange }) => {
  const [logOpen, setLogOpen] = useState(false);
  const status = record?.status || AR_COMMUNICATION_STATUSES.NEW;
  const history = Array.isArray(record?.history) ? record.history : [];
  const last = history.length > 0 ? history[history.length - 1] : null;
  // Partners can't move a line out of an admin-only status
  const locked = !isAdmin && AR_COMMUNICATION_ADMIN_ONLY.includes(status);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <TextField
        select
        size="small"
        value={status}
        onChange={(e) => onChange(status, e.target.value)}
        disabled={locked}
        SelectProps={{ renderValue: (value) => <StatusLabel status={value} /> }}
        sx={{ minWidth: 120, '& .MuiInputBase-root': { height: 40 }, '& .MuiInputBase-input': { py: 1 } }}
      >
        {AR_COMMUNICATION_ORDER.map((value) => (
          <MenuItem
            key={value}
            value={value}
            disabled={!isAdmin && AR_COMMUNICATION_ADMIN_ONLY.includes(value)}
          >
            <StatusLabel status={value} />
          </MenuItem>
        ))}
      </TextField>
      {last && (
        <Tooltip title={`${last.byName || 'Unknown user'} · ${formatWhen(last.at)}`}>
          <Box sx={{ display: 'inline-flex' }}>
            <UserAvatar name={last.byName} color={last.byColor} size={24} />
          </Box>
        </Tooltip>
      )}
      <Tooltip title="Communication log">
        <IconButton size="small" onClick={() => setLogOpen(true)}>
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={logOpen} onClose={() => setLogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Communication log</DialogTitle>
        <DialogContent dividers>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No changes yet — status is New.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[...history].reverse().map((entry, idx) => (
                <Box key={`${entry.at}-${idx}`} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <UserAvatar name={entry.byName} color={entry.byColor} size={28} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>{entry.byName || 'Unknown user'}</Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <StatusLabel status={entry.from || AR_COMMUNICATION_STATUSES.NEW} />
                      <span>→</span>
                      <StatusLabel status={entry.to} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{formatWhen(entry.at)}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ARCommunicationCell;
